import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from './cookie.service';

export interface UserProfile {
  username: string;
  email: string;
  name: string;
  title?: string;
  role?: string;
  locale: string;
  timezone: string;
  measurement: 'Imperial' | 'Metric';
  homeApp: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  private apiUrl = '/api/auth';

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private translate: TranslateService
  ) {}

  getUserProfile(): Observable<UserProfile> {
    return this.http.get<any>(`${this.apiUrl}/me`, { withCredentials: true })
      .pipe(
        map(response => {
          if (!response.user) {
            throw new Error('No user data in response');
          }

          const user = response.user;
          
          return {
            username: this.extractUsername(user),
            email: user.email || user.username || '',
            name: user.name || user.username || 'Unknown User',
            title: user.title || user.role || 'User',
            role: user.role,
            locale: this.getLocale(),
            timezone: this.getTimezone(),
            measurement: this.getMeasurement(),
            homeApp: 'Optima'
          };
        }),
        catchError(error => {
          console.error('💥 [USER-PROFILE] Error fetching profile:', error);
          return of(this.getDefaultProfile());
        })
      );
  }

  private extractUsername(user: any): string {
    if (user.username) {
      return user.username;
    }
    
    if (user.email) {
      const emailParts = user.email.split('@');
      return emailParts[0];
    }

    return 'unknown_user';
  }

  private getLocale(): string {
    return this.cookieService.getLocale() || 'en_US';
  }

  private getTimezone(): string {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'timezone') {
        return decodeURIComponent(value);
      }
    }

    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'America/New_York';
    }
  }

  private getMeasurement(): 'Imperial' | 'Metric' {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'measurement') {
        return value === 'Metric' ? 'Metric' : 'Imperial';
      }
    }
    return 'Imperial';
  }

  private getDefaultProfile(): UserProfile {
    return {
      username: 'unknown',
      email: 'no-email@example.com',
      name: 'Unknown User',
      title: this.translate.instant('user.sheet.title'),
      locale: 'en_US',
      timezone: 'America/New_York',
      measurement: 'Imperial',
      homeApp: 'Optima'
    };
  }

  updatePreferences(userId: string, preferences: { preferences: any }): Observable<any> {
    return this.http.patch(`/api/v1/users/${userId}`, preferences, { withCredentials: true });
  }
}
